import React from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Play, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TypewriterText } from "@/components/TypewriterText";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { ScrollIndicator } from "@/components/ScrollIndicator";
import { useParallax } from "@/hooks/useParallax";

// Poster images from CDN
const POSTER_MOBILE = {
  avif: "https://assets.loverslabs.art/HeroLoversLabs/843face1-8bbe-4ca5-953d-04b93fb386eb.avif",
  webp: "https://assets.loverslabs.art/HeroLoversLabs/f1b470b9-e028-4525-b30f-a63367968238.webp",
};
const POSTER_DESKTOP = {
  avif: "https://assets.loverslabs.art/HeroLoversLabs/e089dedd-1abb-4afb-b1fa-f3a411413f56.avif",
  webp: "https://assets.loverslabs.art/HeroLoversLabs/ec12d4ac-c8b8-4f85-9981-b0d2c3630fea.webp",
};

export function HeroSection() {
  const { t } = useTranslation();
  const parallaxOffset = useParallax(0.3);
  const [shouldLoadVideo, setShouldLoadVideo] = React.useState(false);
  const [videoLoaded, setVideoLoaded] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Detecta mobile real (não apenas CSS)
  const isMobile = React.useMemo(() => {
    return typeof window !== "undefined" && window.innerWidth < 768;
  }, []);

  // Delay video loading to prioritize poster render
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => setShouldLoadVideo(true));
      } else {
        setShouldLoadVideo(true);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // URL do vídeo correto baseado no device real
  const videoUrl = isMobile
    ? "https://assets.loverslabs.art/videos/weboptimizedmobile.mp4"
    : "https://assets.loverslabs.art/hero.mp4";

  const handleVideoReady = () => {
    setVideoLoaded(true);
  };

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background with Parallax */}
      <div className="absolute inset-0 w-full h-full" style={{ transform: `translateY(${parallaxOffset}px)` }}>
        {/* Poster Image - loads immediately */}
        <picture
          className={`absolute inset-0 w-full h-full transition-opacity duration-700 ${
            videoLoaded ? "opacity-0" : "opacity-100"
          }`}
        >
          {/* Mobile AVIF */}
          <source media="(max-width: 767px)" srcSet={POSTER_MOBILE.avif} type="image/avif" />
          {/* Mobile WebP */}
          <source media="(max-width: 767px)" srcSet={POSTER_MOBILE.webp} type="image/webp" />
          {/* Desktop AVIF */}
          <source media="(min-width: 768px)" srcSet={POSTER_DESKTOP.avif} type="image/avif" />
          {/* Desktop WebP */}
          <source media="(min-width: 768px)" srcSet={POSTER_DESKTOP.webp} type="image/webp" />
          <img
            src={isMobile ? POSTER_MOBILE.webp : POSTER_DESKTOP.webp}
            alt="Hero background"
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </picture>

        {/* Video - loads after poster */}
        {shouldLoadVideo && (
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            onCanPlayThrough={handleVideoReady}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
              videoLoaded ? "opacity-100" : "opacity-0"
            }`}
          >
            <source src={videoUrl} type="video/mp4" />
          </video>
        )}

        {/* Overlay Combinado */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(to right, hsl(var(--background) / 0.6), hsl(var(--background) / 0.35), hsl(var(--background) / 0.6)),
              linear-gradient(to bottom, transparent, hsl(var(--background) / 0.55)),
              radial-gradient(circle at 50% 50%, hsl(var(--gold) / 0.015), transparent 70%)
            `,
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-4 md:py-8 text-center h-full flex items-center">
        <div className="max-w-4xl mx-auto space-y-3 md:space-y-4 w-full">
          {/* Badge with Animation */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-premium border border-gold/20 text-sm text-gold animate-float">
            <span className="font-semibold text-gold">#1</span>
            <span className="font-semibold">The world’s best NSFW AI</span>
          </div>

          {/* Logo */}
          <div className="flex justify-center animate-fade-in">
            <img
              src="https://assets.loverslabs.art/galeria-imagens-agion/logoheroBSwh3kw1.png"
              alt="Lovers Labs Logo"
              className="h-24 md:h-32 lg:h-48 w-auto object-contain drop-shadow-2xl pointer-events-none"
              width="725"
              height="192"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              draggable="false"
            />
          </div>

          {/* Subtitle with Typewriter */}
          <div className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed min-h-[28px] md:min-h-[32px]">
            <TypewriterText text={t("hero.subtitle")} speed={30} />
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4 md:pt-6">
            <Link to="/gallery">
              <Button
                size="lg"
                variant="gradient"
                className="text-base md:text-lg px-6 md:px-8 py-4 md:py-5 h-auto group"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                {t("hero.viewGallery")}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>

            <Link to="/generator">
              <Button size="lg" variant="outline" className="text-base md:text-lg px-6 md:px-8 py-4 md:py-5 h-auto">
                <Play className="w-5 h-5 mr-2" />
                {t("hero.createNow")}
              </Button>
            </Link>
          </div>

          {/* Animated Stats - Renderização condicional real */}
          {!isMobile && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-10 pt-12 md:pt-20">
              <StatCard value={18} suffix="+" label={t("hero.stats.ai")} />
              <StatCard value={200} suffix="K+" label={t("hero.stats.art")} />
              <StatCard value={4} suffix="K" label={t("hero.stats.video")} />
            </div>
          )}

          {/* Scroll Indicator */}
          <ScrollIndicator />
        </div>
      </div>
    </section>
  );
}

// Componente separado para melhor performance
const StatCard = ({ value, suffix, label }: { value: number; suffix: string; label: string }) => (
  <div className="text-center p-4 rounded-lg border-premium hover-glow-gold bg-white/10">
    <div className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
      <AnimatedCounter end={value} suffix={suffix} />
    </div>
    <div className="text-sm text-muted-foreground mt-1">{label}</div>
  </div>
);
